import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../Icon';
import clsx from 'clsx';
import { translate } from '../../Services/translation';
import { round } from 'lodash-es';
import NotificationDot from '../NotificationDot';
import { Tooltip } from 'react-tooltip';
import useIsMobile from '../../Hooks/useIsMobile';

export interface MeasuredWallpaperZoomButtonsProps {
  onZoom: (zoom: number) => void;
  minZoomRatio: number;
  maxZoomRatio: number;
  currentZoomRatio: number;
  zoomRatioStep: number;
  visibleZoomLevel: number;
}

const MeasuredWallpaperZoomButtons = ({
  onZoom,
  currentZoomRatio,
  visibleZoomLevel,
  zoomRatioStep,
  minZoomRatio,
  maxZoomRatio,
}: MeasuredWallpaperZoomButtonsProps) => {
  const isMobile = useIsMobile();

  const zoomLevelTooltip = useMemo(
    () =>
      translate('measured-wallpaper.zoom-level').replace(
        '{0}',
        visibleZoomLevel.toFixed(2).toString()
      ),
    [visibleZoomLevel]
  );

  return (
    <>
      <button
        className={clsx(
          'grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-solid border-gray-100 bg-opacity-white-90 text-gray-900 transition-colors duration-300 disabled:text-gray-300',
          currentZoomRatio === 0 && 'pointer-events-none'
        )}
        onClick={() => onZoom(0)}
        data-tooltip-id="zoom-tooltip"
        data-tooltip-content={translate('measured-wallpaper.reset-zoom')}
        disabled={currentZoomRatio === 0}
      >
        <Icon name="frame-corners" className="h-5 w-5" />
      </button>
      <button
        className="relative grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-solid border-gray-100 bg-opacity-white-90 text-gray-900 disabled:text-gray-300"
        onClick={() => onZoom(round(currentZoomRatio - zoomRatioStep, 2))}
        disabled={
          visibleZoomLevel <= minZoomRatio ||
          round(visibleZoomLevel - zoomRatioStep, 2) < minZoomRatio
        }
        data-tooltip-id="zoom-tooltip"
        data-tooltip-content={
          currentZoomRatio != 0 ? zoomLevelTooltip : undefined
        }
      >
        <Icon name="magnifying-glass-minus" className="h-5 w-5" />
        {currentZoomRatio < 0 && <NotificationDot />}
      </button>
      <button
        className="relative grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-solid border-gray-100 bg-opacity-white-90 text-gray-900 disabled:text-gray-300"
        onClick={() => onZoom(round(currentZoomRatio + zoomRatioStep, 2))}
        disabled={
          visibleZoomLevel >= maxZoomRatio ||
          round(visibleZoomLevel + zoomRatioStep, 2) > maxZoomRatio
        }
        data-tooltip-id="zoom-tooltip"
        data-tooltip-content={
          currentZoomRatio != 0 ? zoomLevelTooltip : undefined
        }
      >
        <Icon name="magnifying-glass-plus" className="h-5 w-5" />
        {currentZoomRatio > 0 && <NotificationDot />}
      </button>
      <Tooltip
        id="zoom-tooltip"
        place={isMobile ? 'top' : 'bottom'}
        delayShow={200}
      />
    </>
  );
};

export default MeasuredWallpaperZoomButtons;
